import { useCallback, useRef, useState } from "react";
import { SaucerApiError } from "@saucer/core";
import type { SaucerFile } from "@saucer/core";
import { api } from "./api.js";

export interface UploadJob {
  id: string;
  filename: string;
  size: number;
  uploaded: number;
  status: "uploading" | "done" | "failed";
  error?: string;
  file?: SaucerFile;
}

/** Uploads run one at a time so a folder drop cannot saturate the connection. */
export function useUploads(onUploaded: (file: SaucerFile) => void) {
  const [jobs, setJobs] = useState<UploadJob[]>([]);
  const queue = useRef<Promise<void>>(Promise.resolve());

  const patch = useCallback((id: string, changes: Partial<UploadJob>) => {
    setJobs((current) =>
      current.map((job) => (job.id === id ? { ...job, ...changes } : job)),
    );
  }, []);

  const add = useCallback(
    (files: File[]) => {
      const started = files.map((file) => ({
        id: `${file.name}-${file.size}-${crypto.randomUUID()}`,
        filename: file.name,
        size: file.size,
        uploaded: 0,
        status: "uploading" as const,
      }));
      setJobs((current) => [...started, ...current]);

      started.forEach((job, index) => {
        const file = files[index];
        if (!file) return;
        queue.current = queue.current.then(async () => {
          try {
            const uploaded = await api.upload(file, {
              filename: file.name,
              onProgress: (bytes) => patch(job.id, { uploaded: bytes }),
            });
            patch(job.id, { status: "done", uploaded: file.size, file: uploaded });
            onUploaded(uploaded);
          } catch (error) {
            patch(job.id, { status: "failed", error: describe(error) });
          }
        });
      });
    },
    [onUploaded, patch],
  );

  const dismiss = useCallback((id: string) => {
    setJobs((current) => current.filter((job) => job.id !== id));
  }, []);

  return { jobs, add, dismiss };
}

const describe = (error: unknown): string =>
  error instanceof SaucerApiError ? error.message : "The upload did not finish.";
